import { SearchIcon, UserRoundPlusIcon } from 'lucide-react';
import { RefObject, useMemo } from 'react';

import type { CustomerDTO } from '@invenco/sales-interface/interfaces';

import { Button } from '@/components/core/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxSeparator,
} from '@/components/core/combobox';
import { InputGroupAddon } from '@/components/core/input-group';
import { useGateways } from '@/gateways/GatewayProvider';
import { useAutoCompleteQuery } from '@/shared/hooks/queries';

type CustomerOption = {
  value: string;
  label: string;
  customer: CustomerDTO;
};

export function CustomerCombobox({
  inputRef,
  onSelect,
  onCreateNewCustomer,
}: {
  inputRef: RefObject<HTMLInputElement>;
  onSelect: (customer: CustomerDTO) => void;
  onCreateNewCustomer: () => void;
}) {
  const { salesGateway } = useGateways();
  const {
    options: matchingCustomers,
    onSearch,
    isLoading,
  } = useAutoCompleteQuery({
    parentKey: 'order-wizard-customers',
    query: (input, { signal }) => salesGateway.getCustomers(input, { signal }),
    enabled: true,
  });

  const options = useMemo<CustomerOption[]>(
    () =>
      matchingCustomers.map((customer) => ({
        value: customer.id,
        label: customer.name,
        customer,
      })),
    [matchingCustomers],
  );

  return (
    <Combobox
      filteredItems={options}
      items={options}
      onInputValueChange={(value) => onSearch(typeof value === 'string' ? value : '')}
      onValueChange={(value) => {
        const selectedValue = typeof value === 'string' ? value : undefined;
        if (!selectedValue) return;

        const option = options.find((item) => item.value === selectedValue);
        if (option) onSelect(option.customer);
      }}
    >
      <ComboboxInput ref={inputRef} placeholder="Search customers" aria-label="Customer" showClear>
        <InputGroupAddon align="inline-start">
          <SearchIcon className="size-3.5" />
        </InputGroupAddon>
      </ComboboxInput>
      <ComboboxContent>
        <ComboboxEmpty>{isLoading ? 'Loading customers...' : 'No customers found'}</ComboboxEmpty>
        <ComboboxList>
          {(option: CustomerOption) => (
            <ComboboxItem key={option.value} value={option.value}>
              <span className="min-w-0 flex-1 truncate">{option.customer.name}</span>
            </ComboboxItem>
          )}
        </ComboboxList>
        <ComboboxSeparator />
        <div className="px-1 pb-1">
          <Button
            type="button"
            variant="ghost"
            className="w-full justify-center"
            onClick={onCreateNewCustomer}
          >
            <UserRoundPlusIcon />
            Create new customer
          </Button>
        </div>
      </ComboboxContent>
    </Combobox>
  );
}
